import type { DeviceInfo } from '@/shared/lib/livekit'
import { Field } from '@/shared/ui/field'
import { IconButton } from '@/shared/ui/icon-button'
import { Select } from '@/shared/ui/select'
import { useMicLevel } from '../lib/use-mic-level'
import { useDeviceSelection } from '../model/device-store'
import { MicLevelMeter } from './mic-level-meter'
import { SpeakerTestButton } from './speaker-test-button'

interface DevicePickerProps {
  cameras: DeviceInfo[]
  microphones: DeviceInfo[]
  // Устройства вывода: пустой список (нет setSinkId, напр. Safari) — селект скрыт.
  speakers?: DeviceInfo[]
  // В звонке вкл/выкл живёт в тулбаре — пикер показывает только выбор устройств.
  showToggles?: boolean
  // Проверка микрофона и динамиков — только до входа: в звонке микрофон уже
  // захвачен комнатой, и второй захват под индикатор отбирал бы у неё устройство.
  withChecks?: boolean
}

// Пустое значение = системное устройство по умолчанию: оно следует за системой,
// поэтому воткнутая гарнитура подхватывается сама.
const SYSTEM_DEFAULT = 'Системное по умолчанию'

function deviceLabel(device: DeviceInfo, fallback: string): string {
  return device.label || `${fallback} ${device.deviceId.slice(0, 6)}`
}

export function DevicePicker({
  cameras,
  microphones,
  speakers = [],
  showToggles = true,
  withChecks = false,
}: DevicePickerProps) {
  const camId = useDeviceSelection((s) => s.camId)
  const micId = useDeviceSelection((s) => s.micId)
  const outputId = useDeviceSelection((s) => s.outputId)
  const camEnabled = useDeviceSelection((s) => s.camEnabled)
  const micEnabled = useDeviceSelection((s) => s.micEnabled)
  const setCamId = useDeviceSelection((s) => s.setCamId)
  const setMicId = useDeviceSelection((s) => s.setMicId)
  const setOutputId = useDeviceSelection((s) => s.setOutputId)
  const setCamEnabled = useDeviceSelection((s) => s.setCamEnabled)
  const setMicEnabled = useDeviceSelection((s) => s.setMicEnabled)
  const micLevel = useMicLevel(withChecks && micEnabled, micId)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Field label="Камера">
            {(p) => (
              <Select
                {...p}
                value={camId ?? ''}
                placeholder={SYSTEM_DEFAULT}
                options={cameras.map((d) => ({
                  value: d.deviceId,
                  label: deviceLabel(d, 'Камера'),
                }))}
                onChange={(value) => setCamId(value || undefined)}
              />
            )}
          </Field>
        </div>
        {showToggles && (
          <IconButton
            icon={camEnabled ? 'camera' : 'camera-off'}
            label={camEnabled ? 'Выключить камеру' : 'Включить камеру'}
            active={!camEnabled}
            tone="danger"
            onClick={() => setCamEnabled(!camEnabled)}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Field label="Микрофон">
              {(p) => (
                <Select
                  {...p}
                  value={micId ?? ''}
                  placeholder={SYSTEM_DEFAULT}
                  options={microphones.map((d) => ({
                    value: d.deviceId,
                    label: deviceLabel(d, 'Микрофон'),
                  }))}
                  onChange={(value) => setMicId(value || undefined)}
                />
              )}
            </Field>
          </div>
          {showToggles && (
            <IconButton
              icon={micEnabled ? 'mic' : 'mic-off'}
              label={micEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
              active={!micEnabled}
              tone="danger"
              onClick={() => setMicEnabled(!micEnabled)}
            />
          )}
        </div>
        {withChecks && <MicLevelMeter level={micLevel} micEnabled={micEnabled} />}
      </div>

      <div className="flex flex-col gap-2">
        {speakers.length > 0 && (
          <Field label="Динамики">
            {(p) => (
              <Select
                {...p}
                value={outputId ?? ''}
                placeholder={SYSTEM_DEFAULT}
                options={speakers.map((d) => ({
                  value: d.deviceId,
                  label: deviceLabel(d, 'Динамики'),
                }))}
                onChange={(value) => setOutputId(value || undefined)}
              />
            )}
          </Field>
        )}
        {/* Проверка звука нужна и там, где выбора устройства нет: вопрос «слышу
            ли я вообще» от наличия setSinkId не зависит. */}
        {withChecks && (
          <div className="flex justify-start">
            <SpeakerTestButton outputId={outputId} />
          </div>
        )}
      </div>
    </div>
  )
}
